/**
 * Material item Unit Aggregate：unit.base／unit.convert（單一 object｜null）。
 * 與子資源 to_base_* 分開；主檔儲存走本 helper。
 */

/**
 * 無進位單位時後端回 `{ id: null, name: null, convertRate: 1 }` — 不當成有效進位。
 * nested unit 沒有 code。
 */
export function isEmptyConvertProjection(convert) {
  if (convert == null) {
    return true;
  }
  if (Array.isArray(convert)) {
    return convert.length === 0
      || convert.every((row) => isEmptyConvertProjection(row));
  }
  if (typeof convert !== 'object') {
    return true;
  }

  const hasId = convert.id != null && String(convert.id).trim() !== '';
  const hasName = convert.name != null && String(convert.name).trim() !== '';
  return !hasId && !hasName;
}

function normalizeConvertToRows(convert) {
  if (isEmptyConvertProjection(convert)) {
    return [];
  }
  if (Array.isArray(convert)) {
    return convert.filter((row) => !isEmptyConvertProjection(row)).slice(0, 1);
  }
  return [convert];
}

export function mapDetailUnitToForm(detail) {
  const unit = detail?.unit;
  const base = unit?.base ?? null;

  const convertRows = normalizeConvertToRows(unit?.convert);

  return {
    unitBaseId: base?.id ?? '',
    baseUnitName: base?.name ?? '',
    unitConvert: convertRows.map((row) => ({
      id: row?.id ?? '',
      name: row?.name ?? '',
      convertRate: row?.convertRate != null ? String(row.convertRate) : '',
    })),
    _baseUnitInitialized: Boolean(base?.id),
  };
}

/**
 * Form `unitConvert[]`（最多一筆）→ API `unit.convert` object｜null。
 * 移除唯一進位單位時送 null。不送 code。
 */
export function serializeUnitConvert(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  for (const row of list) {
    const name = String(row?.name ?? '').trim();
    const rate = Number(row?.convertRate);
    if (!name || !Number.isFinite(rate) || rate <= 0) {
      continue;
    }

    const item = {
      name,
      convertRate: rate,
    };
    const id = String(row?.id ?? '').trim();
    if (id) {
      item.id = id;
    }
    return item;
  }
  return null;
}

/**
 * @param {object} values — form values
 * @returns {{ base: object|null, convert: object|null }|null|undefined}
 *   undefined＝省略（GROUP PATCH）；null＝DRAFT 明示空
 */
export function buildUnitAggregatePayload(values) {
  const isGroup = values.mtrKind === 'GROUP';
  if (isGroup) {
    return undefined;
  }

  const baseId = String(values.unitBaseId ?? '').trim();
  const baseName = String(values.baseUnitName ?? '').trim();
  const convert = serializeUnitConvert(values.unitConvert);

  if (baseId) {
    return {
      base: { id: baseId, name: baseName },
      convert,
    };
  }

  if (baseName) {
    return {
      base: { name: baseName },
      convert,
    };
  }

  return null;
}
